import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  FileStack, Plus, Search, ChevronLeft, ChevronRight, GripVertical, RefreshCw,
  Send, CheckCircle, GitBranch, FileDown, ListChecks, CheckCheck, Pencil, X, Save,
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { dsPackageApi } from '../../services/api';
import { formatDateOnly, formatTimestampDate } from '../../utils/dateTime';
import DsPackageCreateModal from './DsPackageCreateModal';
import DsPackageRecordsPanel from './DsPackageRecordsPanel';
import IndexSheetPackageSection from './IndexSheetPackageSection';
import TransmittalLogSection from './TransmittalLogSection';
import DsPackageExportModal from './DsPackageExportModal';
import DsPackageReviseModal from './DsPackageReviseModal';
import CollapsibleSectionHeader from './CollapsibleSectionHeader';

/**
 * Submittal Package (the ISA datasheet package) — generation, review and publish.
 *
 * Lives under Project Management > Transmittals, gated on the per-database key
 * projectmanagement.transmittals (route guard in App.js, DsPackageController on the API).
 *
 * Layout follows AdminUsersPage: own module header (icon + title + meta + CRUD + 6-dot action
 * menu), then the STUI section-26 two-panel list view — searchable, collapsible package list on
 * the left, detail and records on the right. Header is hand-built rather than ModuleHeader
 * because the workflow verbs belong in the 6-dot, and there is no grid here to filter.
 */

// Status vocabularies are code-owned, not lookups: the DB CHECK constraints and the server's
// transition graph are the real gate, so a value added through the UI would be rejected with no
// edge to reach it. Only DS_ExecType is a lookup.
//
// Record status counts, in workflow order. "DR" is Returned — the approver sent it back.
const RECORD_ORDER = [
  ['D',  'Development'],
  ['R',  'In Review'],
  ['DR', 'Returned'],
  ['A',  'Approved'],
];

const PACKAGE_STATUS = {
  'In Development': 'stui-badge-gray',
  'In Review':      'stui-badge-amber',
  'Approved':       'stui-badge-green',
};

// The four workflow stages, in the order they happen. Keys match the `workflow` object the
// API returns, which is the DSPackage.WF_*_At columns.
const WORKFLOW_STAGES = [
  ['indexSheets',          'Index Sheets'],
  ['datasheets',           'ISA Datasheets'],
  ['transmittalGenerated', 'Submittal Transmittal Generated'],
  ['submittedApproved',    'Submitted & Approved'],
];

/**
 * Workflow flag strip.
 *
 * A stage is complete when its timestamp is set — there is no separate flag column, so the
 * date and the state cannot disagree. Complete stages read green and show when; the rest sit
 * gray and say Pending, because "no date yet" is the honest answer, not a blank.
 *
 * Built from stui-field-group / stui-col-6 rather than anything new: it is four labelled
 * read-only values, which is exactly what the rest of the panel already is.
 */
const WorkflowStrip = ({ workflow }) => (
  <div className="stui-detail-grid">
    <div className="stui-col-24">
      <div className="stui-detail-section-header">Workflow</div>
    </div>
    {WORKFLOW_STAGES.map(([key, label]) => {
      const at = workflow?.[key];
      return (
        <div className="stui-field-group stui-col-6" key={key}>
          <label>{label}</label>
          <div className="stui-field-display">
            <span className={`stui-badge ${at ? 'stui-badge-green' : 'stui-badge-gray'}`}>
              {at ? formatTimestampDate(at) : 'Pending'}
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

export default function DsPackagePage() {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const menuRef = useRef(null);

  // Every write is attributed. The server refuses one it cannot pin to a person, because
  // DS_PackageDrafter, DS_PackageApprover and Rev_User record who did what.
  const auditCfg = useMemo(
    () => ({ headers: { 'X-Changed-By': currentUser?.email || '' } }),
    [currentUser]
  );

  const [packages, setPackages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [listCollapsed, setListCollapsed] = useState(false);
  // Independent collapse state for each collapsible detail-panel section — all start expanded.
  const [sectionCollapsed, setSectionCollapsed] = useState({
    pkgInfo: false, indexSheet: false, datasheet: false, transmittal: false,
  });
  const toggleSection = (key) => setSectionCollapsed(s => ({ ...s, [key]: !s[key] }));
  const [searchTerm, setSearchTerm] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showRevise, setShowRevise] = useState(false);
  // { recordIds: null } = whole package; { recordIds: [...], label } = a subset.
  const [exportScope, setExportScope] = useState(null);

  const loadPackages = useCallback(async () => {
    try {
      const res = await dsPackageApi.list();
      setPackages(res.data || []);
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Failed to load submittal packages.');
    }
  }, [notify]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  // Packages are scoped to one project DB, so a stale list would show another project's
  // documents after a database switch.
  useEffect(() => {
    const onDbChange = () => { setSelectedId(null); loadPackages(); };
    window.addEventListener('pmd-database-changed', onDbChange);
    return () => window.removeEventListener('pmd-database-changed', onDbChange);
  }, [loadPackages]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return packages;
    return packages.filter(p =>
      [p.dsPackage, p.dsPackageDesc, p.sourceLabel, p.dsPackageDrafter, p.dsPackageApprover]
        .some(v => (v || '').toLowerCase().includes(term))
    );
  }, [packages, searchTerm]);

  const selected = packages.find(p => p.id === selectedId) || null;

  // Description and revision remarks are the only hand-written fields on a package; the rest
  // is workflow-owned. Editing is refused once the package is Approved, here and on the server.
  const [editText, setEditText] = useState(null);   // { desc, remarks } while editing
  const [savingText, setSavingText] = useState(false);
  const isApproved = selected?.dsStatus === 'Approved';
  useEffect(() => { setEditText(null); }, [selectedId]);
  const textDirty = key => editText !== null
    && editText[key] !== ((key === 'desc' ? selected?.dsPackageDesc : selected?.dsPackageRemarks) || '');
  const startEdit = () => setEditText({
    desc: selected?.dsPackageDesc || '',
    remarks: selected?.dsPackageRemarks || '',
  });
  const saveText = async () => {
    if (!selected || !editText) return;
    setSavingText(true);
    try {
      await dsPackageApi.updateText(selected.id,
        { dsPackageDesc: editText.desc, dsPackageRemarks: editText.remarks }, auditCfg);
      await loadPackages();
      setEditText(null);
      notify.success('Package updated.');
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Save failed.');
    } finally {
      setSavingText(false);
    }
  };

  /**
   * Bulk record status straight from the six-dot. Resolves the ids itself rather than reaching
   * into the records panel, so the menu works whatever the panel is currently showing.
   * One all-or-nothing call, as everywhere else — a half-applied batch is unreadable afterwards.
   */
  const bulkRecordStatus = async (from, to, label) => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await dsPackageApi.records(selected.id);
      const ids = (res.data.rows || []).filter(r => r.dsStatus === from).map(r => r.id);
      if (ids.length === 0) { notify.info(`No records ${label}.`); return; }
      const upd = await dsPackageApi.setRecordStatus(selected.id, ids, to, auditCfg);
      notify.success(`${upd.data.updated} record${upd.data.updated !== 1 ? 's' : ''} → ${to}`);
      await loadPackages();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.rejected?.length) {
        notify.error(`Nothing changed. ${data.rejected.slice(0, 3).join(' ')}`);
      } else {
        notify.error(data?.error || `Could not update records.`);
      }
    } finally {
      setBusy(false);
    }
  };

  const advancePackage = async (pkg, status) => {
    setBusy(true);
    try {
      await dsPackageApi.setStatus(pkg.id, status, auditCfg);
      notify.success(`${pkg.dsPackage} → ${status}`);
      await loadPackages();
    } catch (err) {
      const data = err?.response?.data;
      // A 409 carries blockedReason — the count of records not yet ready, which is the useful
      // part.
      notify.error(data?.blockedReason || data?.error || `Could not move to ${status}.`);
    } finally {
      setBusy(false);
    }
  };

  const issueRevision = async (remarks) => {
    setBusy(true);
    try {
      const res = await dsPackageApi.revise(selected.id, remarks, auditCfg);
      notify.success(
        `${selected.dsPackage} → rev ${res.data.newRev}`
        + (res.data.added ? `, ${res.data.added} tag(s) added` : '')
        + (res.data.reissued ? `, ${res.data.reissued} re-issued` : '')
      );
      setShowRevise(false);
      await loadPackages();
    } catch (err) {
      notify.error(err?.response?.data?.error || 'Could not issue a revision.');
    } finally {
      setBusy(false);
    }
  };

  const approvedCount = packages.filter(p => p.dsStatus === 'Approved').length;

  // A disabled menu item that silently does nothing is worse than no item. `reason` becomes the
  // tooltip so hovering says why it cannot be clicked.
  const menuItem = (key, icon, label, disabled, onClick, reason) => (
    <button
      key={key}
      className={`stui-action-menu-item ${disabled ? 'disabled' : ''}`}
      disabled={disabled}
      title={disabled ? (reason || '') : ''}
      onClick={() => { setMenuOpen(false); onClick(); }}
    >
      {icon}<span>{label}</span>
    </button>
  );

  // Mirrors the server's gate. Approve is reachable from In Development too, once every record
  // is Approved — a detour through In Review adds nothing when there is nothing left to review.
  const c = selected?.counts;
  const allApproved = !!c && c.total > 0 && c.a === c.total;
  const canApprovePkg = !!selected && selected.dsStatus !== 'Approved'
    && (selected.dsStatus === 'In Review' || allApproved);
  const approveReason = !selected ? 'Select a package.'
    : selected.dsStatus === 'Approved' ? 'Already approved.'
    : `${c.total - c.a} of ${c.total} records are not yet Approved.`;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <FileStack size={20} />
          <h1>Submittal Package</h1>
          <span className="stui-module-header-meta">
            {packages.length
              ? `${approvedCount} approved / ${packages.length} package${packages.length === 1 ? '' : 's'}`
              : ''}
          </span>
        </div>

        <div className="stui-module-header-actions">
          {/* No Trash: a package is a controlled document set, not a scratch record. The Pencil
              edits only the description and revision remarks; number, revision and status are
              workflow-driven. */}
          <button
            className="stui-crud-btn"
            onClick={() => setShowCreate(true)}
            title="New package"
            disabled={editText !== null}
          >
            <Plus size={18} />
          </button>
          {editText === null ? (
            <button
              className="stui-crud-btn"
              onClick={startEdit}
              disabled={!selected || isApproved}
              title={isApproved ? 'An approved package cannot be edited' : 'Edit description and remarks'}
            >
              <Pencil size={18} />
            </button>
          ) : (
            <>
              <button className="stui-crud-btn" onClick={() => setEditText(null)} title="Cancel" disabled={savingText}>
                <X size={18} />
              </button>
              <button
                className="stui-crud-btn"
                onClick={saveText}
                title="Save"
                disabled={savingText || !(textDirty('desc') || textDirty('remarks'))}
              >
                <Save size={18} />
              </button>
            </>
          )}

          {/* Workflow verbs live in the 6-dot, per the CRUD standard. The server re-validates
              every one and refuses with a reason, so these disabled states are convenience. */}
          <div className="stui-action-menu" ref={menuRef}>
            <button
              className="stui-action-menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              title="Actions"
            >
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                {menuItem('refresh', <RefreshCw size={16} />, 'Refresh', false, loadPackages)}
                {menuItem('export', <FileDown size={16} />, 'Download PDF…',
                  busy || !selected || selected.counts.total === 0,
                  () => setExportScope({ recordIds: null }))}
                <div className="stui-action-menu-divider" />
                {menuItem('submit', <Send size={16} />, 'Submit Package',
                  busy || !selected || selected.dsStatus !== 'In Development',
                  () => advancePackage(selected, 'In Review'),
                  selected ? `Package is ${selected.dsStatus}.` : 'Select a package.')}
                {menuItem('approve', <CheckCircle size={16} />, 'Approve Package',
                  busy || !canApprovePkg,
                  () => advancePackage(selected, 'Approved'),
                  approveReason)}
                {menuItem('revise', <GitBranch size={16} />, 'Issue Revision…',
                  busy || !selected || selected.dsStatus === 'Approved',
                  () => setShowRevise(true))}
                <div className="stui-action-menu-divider" />
                {menuItem('submitAll', <ListChecks size={16} />,
                  `Submit All in Dev${selected ? ` (${selected.counts.d})` : ''}`,
                  busy || !selected || selected.counts.d === 0,
                  () => bulkRecordStatus('D', 'R', 'in Development'),
                  'No records are in Development.')}
                {menuItem('approveAll', <CheckCheck size={16} />,
                  `Approve All in Review${selected ? ` (${selected.counts.r})` : ''}`,
                  busy || !selected || selected.counts.r === 0,
                  () => bulkRecordStatus('R', 'A', 'in Review'),
                  'No records are In Review.')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {/* LIST PANEL */}
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button
              onClick={() => setListCollapsed(false)}
              className="stui-list-panel-expand-btn"
              title="Expand list"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button
                onClick={() => setListCollapsed(true)}
                className="stui-list-panel-collapse-btn"
                title="Collapse list"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input
                  className="stui-field-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search packages..."
                />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">
                  {packages.length === 0 ? 'No packages yet' : 'No packages found'}
                </div>
              ) : filtered.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`stui-list-item ${selected?.id === p.id ? 'selected' : ''}`}
                >
                  <span className="stui-list-item-primary">{p.dsPackage}</span>
                  <span className="stui-list-item-secondary">
                    Rev {p.dsPackageRev} · {p.dsStatus} · {p.counts.a}/{p.counts.total}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DETAIL PANEL */}
        <div className="stui-detail-panel">
          {selected ? (
            <div className="stui-detail-content">

              <WorkflowStrip workflow={selected.workflow} />

              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <CollapsibleSectionHeader
                    label="Package Information"
                    collapsed={sectionCollapsed.pkgInfo}
                    onToggle={() => toggleSection('pkgInfo')}
                  />
                </div>
                {!sectionCollapsed.pkgInfo && <>
                  <div className="stui-field-group stui-col-8">
                    <label>Package Number</label>
                    <input className="stui-field-input" value={selected.dsPackage} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-4">
                    <label>Revision</label>
                    <input className="stui-field-input" value={selected.dsPackageRev} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-6">
                    <label>Revision Date</label>
                    <input
                      className="stui-field-input"
                      value={formatDateOnly(selected.dsPackageRevDate)}
                      readOnly
                    />
                  </div>
                  <div className="stui-field-group stui-col-6">
                    <label>Status</label>
                    <div className="stui-field-display">
                      <span className={`stui-badge ${PACKAGE_STATUS[selected.dsStatus] || 'stui-badge-gray'}`}>
                        {selected.dsStatus}
                      </span>
                    </div>
                  </div>

                  <div className="stui-field-group stui-col-12">
                    <label>Source</label>
                    <input className="stui-field-input" value={selected.sourceLabel || ''} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-6">
                    <label>Exec Type</label>
                    <input className="stui-field-input" value={selected.dsExecType} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-6">
                    <label>Datasheets</label>
                    <input className="stui-field-input" value={selected.counts.total} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-24">
                    <label>Description</label>
                    <input
                      className={`stui-field-input${textDirty('desc') ? ' dirty' : ''}`}
                      value={editText ? editText.desc : (selected.dsPackageDesc || '')}
                      readOnly={!editText}
                      maxLength={500}
                      onChange={e => setEditText(t => ({ ...t, desc: e.target.value }))}
                    />
                  </div>
                  <div className="stui-field-group stui-col-24">
                    <label>Revision Remarks</label>
                    <input
                      className={`stui-field-input${textDirty('remarks') ? ' dirty' : ''}`}
                      value={editText ? editText.remarks : (selected.dsPackageRemarks || '')}
                      readOnly={!editText}
                      maxLength={1000}
                      onChange={e => setEditText(t => ({ ...t, remarks: e.target.value }))}
                    />
                  </div>
                </>}

                <div className="stui-col-24">
                  <CollapsibleSectionHeader
                    label="Transmittal Log Entry"
                    collapsed={sectionCollapsed.transmittal}
                    onToggle={() => toggleSection('transmittal')}
                  />
                </div>
                <TransmittalLogSection
                  packageId={selected.id}
                  notify={notify}
                  hidden={sectionCollapsed.transmittal}
                />

                <div className="stui-col-24">
                  <CollapsibleSectionHeader
                    label="Datasheet Package"
                    collapsed={sectionCollapsed.datasheet}
                    onToggle={() => toggleSection('datasheet')}
                  />
                </div>
                {/* Drafters and approvers are both many. The API returns arrays; the joined
                    string is only how they read on a read-only form. */}
                {!sectionCollapsed.datasheet && <>
                  <div className="stui-field-group stui-col-12">
                    <label>{(selected.drafters?.length ?? 0) > 1 ? 'Drafters' : 'Drafter'}</label>
                    <input
                      className="stui-field-input"
                      value={(selected.drafters ?? []).join(', ')}
                      readOnly
                    />
                  </div>
                  <div className="stui-field-group stui-col-12">
                    <label>{(selected.approvers?.length ?? 0) > 1 ? 'Approvers' : 'Approver'}</label>
                    <input
                      className="stui-field-input"
                      value={(selected.approvers ?? []).join(', ')}
                      placeholder="Not yet approved"
                      readOnly
                    />
                  </div>
                  {/* Four plain counts rather than a row of badges: these are quantities, and
                      they read like every other field on the form. */}
                  {RECORD_ORDER.map(([code, label]) => (
                    <div className="stui-field-group stui-col-6" key={code}>
                      <label>{label}</label>
                      <input
                        className="stui-field-input"
                        value={selected.counts[code.toLowerCase()]}
                        readOnly
                      />
                    </div>
                  ))}
                </>}

              </div>

              <DsPackageRecordsPanel
                pkg={selected}
                auditCfg={auditCfg}
                onChanged={loadPackages}
                onExport={(recordIds, label) => setExportScope({ recordIds, label })}
              />

              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <CollapsibleSectionHeader
                    label="Index Sheet Package"
                    collapsed={sectionCollapsed.indexSheet}
                    onToggle={() => toggleSection('indexSheet')}
                  />
                </div>
                {/* Hidden, not unmounted: it holds the chosen output mode and any export in flight. */}
                <IndexSheetPackageSection pkg={selected} hidden={sectionCollapsed.indexSheet} />
              </div>
            </div>
          ) : (
            <div className="stui-detail-empty">
              {packages.length === 0
                ? 'No submittal packages in this project database yet. Use + to create one.'
                : 'Select a package to review its datasheets.'}
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <DsPackageCreateModal
          auditCfg={auditCfg}
          onClose={() => setShowCreate(false)}
          onCreated={(result) => {
            notify.success(
              `Package ${result.dsPackage} created — ${result.generated} record${result.generated === 1 ? '' : 's'}`
              + (result.skipped ? `, ${result.skipped} skipped` : '')
            );
            loadPackages();
            setSelectedId(result.id);
          }}
        />
      )}

      {selected && showRevise && (
        <DsPackageReviseModal
          pkg={selected}
          busy={busy}
          onClose={() => setShowRevise(false)}
          onConfirm={issueRevision}
        />
      )}

      {selected && exportScope && (
        <DsPackageExportModal
          pkg={selected}
          recordIds={exportScope.recordIds}
          scopeLabel={exportScope.label}
          onClose={() => setExportScope(null)}
        />
      )}
    </div>
  );
}
